import {
  IconArrowsDownUp,
  IconBrandChrome,
  IconBrandGoogleFilled,
  IconClock,
  IconCode,
  IconCopy,
  IconEye,
  IconFile,
  IconHttpGet,
  IconMail,
  IconMailOpened,
  IconPlayerRecordFilled,
  IconPointer,
  IconArrowFork,
  IconRefresh,
  IconSearch,
  IconVariable,
  IconWorld,
  IconPlayerStopFilled,
} from '@tabler/icons-react';
import type { JSX } from 'react';
import IconAIEdit from '~/components/icons/IconAIEdit';

export type AddOperationOption = {
  value: string;
  label: string;
  icon: JSX.Element;
  description?: string;
  group?: string;
  isWebOnly?: boolean;
  searchTerms?: string[];
};

const ICON_SIZE = 15;

export const CUSTOM_ACTIONS_OPTION: AddOperationOption = {
  value: 'custom',
  label: 'Add with AI',
  icon: <IconAIEdit className="h-4 w-4 text-gray-700 dark:text-gray-300" />,
  description: 'AI will convert your description into test steps',
};

export const OPERATION_OPTIONS: AddOperationOption[] = [
  {
    value: 'record',
    label: 'Record Actions',
    icon: (
      <IconPlayerRecordFilled
        size={ICON_SIZE}
        color="red"
      />
    ),
    description: 'AI will convert your actions into test steps',
  },
  {
    value: 'copy-steps',
    label: 'Copy Steps',
    icon: <IconCopy size={ICON_SIZE} />,
    description: 'Copy steps from another test',
  },
  {
    value: 'ai-operation',
    label: 'AI Act',
    icon: (
      <IconPointer
        size={ICON_SIZE}
        className="text-purple-500"
      />
    ),
    description: 'Let AI perform custom actions based on a prompt',
    group: 'Actions',
  },
  {
    value: 'ai-assertion',
    label: 'AI Assert',
    icon: (
      <IconEye
        size={ICON_SIZE}
        className="text-purple-500"
      />
    ),
    description: 'Verify the page content or variables with AI',
    group: 'Actions',
  },
  {
    value: 'extract-visual',
    label: 'AI Extract',
    icon: (
      <IconSearch
        size={ICON_SIZE}
        className="text-purple-500"
      />
    ),
    description: 'Extract visual content from the page with AI',
    group: 'Actions',
  },
  {
    value: 'code',
    label: 'Add Code',
    icon: <IconCode size={ICON_SIZE} />,
    description: 'Write custom code to perform specific actions',
    group: 'Actions',
  },
  {
    value: 'conditional',
    label: 'Conditional Block',
    icon: <IconArrowFork size={ICON_SIZE} />,
    description: 'Execute different operations based on a condition',
    group: 'Actions',
  },
  {
    value: 'empty',
    label: 'Add Empty Step',
    icon: <IconFile size={ICON_SIZE} />,
    description: 'Create a blank step to fill in later',
    group: 'Actions',
  },
  {
    value: 'navigate',
    label: 'Navigate to a URL',
    icon: <IconWorld size={ICON_SIZE} />,
    description: 'Go to a specific webpage',
    group: 'Actions',
    isWebOnly: true,
  },
  {
    value: 'wait',
    label: 'Wait for a duration',
    icon: <IconClock size={ICON_SIZE} />,
    description: 'Pause execution for a specified time',
    group: 'Actions',
  },
  {
    value: 'refresh',
    label: 'Reload Page',
    icon: <IconRefresh size={ICON_SIZE} />,
    description: 'Refresh the page',
    group: 'Actions',
    isWebOnly: true,
  },
  {
    value: 'scroll',
    label: 'Scroll',
    icon: <IconArrowsDownUp size={ICON_SIZE} />,
    description: 'Scroll the page in a specific direction',
    group: 'Actions',
    isWebOnly: true,
  },
  {
    value: 'variable',
    label: 'Variable',
    icon: <IconVariable size={ICON_SIZE} />,
    description: 'Define or extract a variable',
    group: 'Actions',
  },
  {
    value: 'setup-email-inbox',
    label: 'Setup Email Inbox',
    icon: <IconMail size={ICON_SIZE} />,
    description: 'Get a test email address from Stably to receive emails',
    group: 'Actions',
  },
  {
    value: 'receive-email',
    label: 'Receive and Extract Email',
    icon: <IconMailOpened size={ICON_SIZE} />,
    description: 'Extract content from the latest email',
    group: 'Actions',
  },
  {
    value: 'fetch-request',
    label: 'Fetch Request',
    icon: <IconHttpGet size={ICON_SIZE} />,
    description: 'Execute a fetch request to a URL',
    group: 'Actions',
  },
  {
    value: 'add-extension',
    label: 'Install Chrome Extension',
    description: 'Install a pre-uploaded Chrome Extension onto your virtual browser',
    icon: <IconBrandChrome size={ICON_SIZE} />,
    isWebOnly: true,
    group: 'Actions',
  },
  {
    value: 'end-test',
    label: 'End Test',
    icon: <IconPlayerStopFilled size={ICON_SIZE} />,
    description: 'Terminate the test execution early',
    group: 'Actions',
  },
  {
    value: 'special-login-google',
    label: 'Login to Google',
    icon: <IconBrandGoogleFilled size={ICON_SIZE} />,
    description: 'Login to Google with a test account',
  },
];
